import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.js';
import { useMembers } from '../lib/queries.js';
import {
  type OnboardingRole,
  type OnboardingState,
  ACTION_STEPS,
  ROUTE_STEP_MAP,
  getChecklistForRole,
  isOnboardingEnabled,
  loadOnboardingState,
  saveOnboardingState,
  setOnboardingSentryTags,
  trackOnboardingBreadcrumb,
} from '../lib/onboarding.js';

export interface OnboardingProgress {
  /** Whether onboarding system is enabled */
  isEnabled: boolean;
  /** Detected user role */
  role: OnboardingRole;
  /** Checklist steps with completion status */
  steps: Array<{ key: string; labelKey: string; completed: boolean }>;
  /** Completed count */
  completedCount: number;
  /** Total count */
  totalCount: number;
  /** Completion percentage 0-100 */
  percent: number;
  /** Whether checklist was dismissed by user */
  checklistDismissed: boolean;
  /** Whether all steps are complete */
  allComplete: boolean;
  /** Mark a step as completed */
  completeStep: (stepKey: string) => void;
  /** Dismiss the checklist card */
  dismissChecklist: () => void;
}

export function useOnboardingProgress(): OnboardingProgress {
  const { user } = useAuth();
  const { data: members } = useMembers();
  const location = useLocation();

  const orgId = user?.orgId ?? 0;

  // Detect role
  const role = useMemo<OnboardingRole>(() => {
    if (!user || !members) return 'execution';
    const myMember = members.find((d) => d.userId === user.id);
    return myMember?.level?.canSupervise ? 'coordination' : 'execution';
  }, [user, members]);

  // Load persisted state
  const [state, setState] = useState<OnboardingState>(() => loadOnboardingState(orgId));

  // Reload state when org changes
  const prevOrgId = useRef(orgId);
  useEffect(() => {
    if (orgId !== prevOrgId.current) {
      setState(loadOnboardingState(orgId));
      prevOrgId.current = orgId;
    }
  }, [orgId]);

  // Checklist for current role
  const checklist = useMemo(() => getChecklistForRole(role), [role]);

  // Build steps with completion status
  const steps = useMemo(
    () =>
      checklist.map((s) => ({
        key: s.key,
        labelKey: s.labelKey,
        completed: state.completedSteps.includes(s.key),
      })),
    [checklist, state.completedSteps],
  );

  const completedCount = steps.filter((s) => s.completed).length;
  const totalCount = steps.length;
  const percent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
  const allComplete = completedCount === totalCount;

  // Persist helper
  const persist = useCallback(
    (next: OnboardingState) => {
      setState(next);
      if (orgId) saveOnboardingState(orgId, next);
    },
    [orgId],
  );

  // Complete a step
  const completeStep = useCallback(
    (stepKey: string) => {
      setState((prev) => {
        if (prev.completedSteps.includes(stepKey)) return prev;
        const next: OnboardingState = {
          ...prev,
          completedSteps: [...prev.completedSteps, stepKey],
        };
        if (orgId) saveOnboardingState(orgId, next);
        trackOnboardingBreadcrumb('checklist_item_completed', { step: stepKey, role });
        return next;
      });
    },
    [orgId, role],
  );

  // Dismiss checklist
  const dismissChecklist = useCallback(() => {
    const next: OnboardingState = {
      ...state,
      checklistDismissedAt: new Date().toISOString(),
    };
    persist(next);
    trackOnboardingBreadcrumb('checklist_dismissed', { role, progress: `${completedCount}/${totalCount}` });
  }, [state, persist, role, completedCount, totalCount]);

  // Auto-complete on pageview
  useEffect(() => {
    if (!isOnboardingEnabled() || !orgId) return;
    const stepsForRoute = ROUTE_STEP_MAP[location.pathname];
    if (!stepsForRoute) return;

    for (const stepKey of stepsForRoute) {
      // Skip action-only steps (they need POST success, not just pageview)
      if (ACTION_STEPS.has(stepKey)) continue;
      completeStep(stepKey);
    }
  }, [location.pathname, orgId, completeStep]);

  // Set Sentry tags
  useEffect(() => {
    if (isOnboardingEnabled()) {
      setOnboardingSentryTags(role, `${completedCount}/${totalCount}`);
    }
  }, [role, completedCount, totalCount]);

  return {
    isEnabled: isOnboardingEnabled(),
    role,
    steps,
    completedCount,
    totalCount,
    percent,
    checklistDismissed: state.checklistDismissedAt !== null,
    allComplete,
    completeStep,
    dismissChecklist,
  };
}
